// Where a HISTORICAL model's output goes: a dataset, always (docs/architecture/HISTORICAL_DATA_RUNNERS.md). Either a
// new one the wizard makes on deploy (the default: one dataset per output, named "<model> · <output>") or an existing
// historical dataset the owner may write. A model no longer stores historical data itself (data pipelines
// convergence plan, decision 3). Pure, so the wizard, the deploy pipeline and Jest share one copy.

export type OutputDestination = "NEW_DATASET" | "DATASET";

/** A ticked output's wizard value: the checkbox, its unit, and where it goes. */
export interface OutputDestinationValue {
  value?: boolean;
  unit?: string;
  /** Absent = NEW_DATASET. "MODEL" is what the retired "Model output" choice stored; it is refused at deploy. */
  destination?: OutputDestination | "MODEL";
  /** DATASET: the existing dataset to write */
  datasetId?: string | null;
  /** NEW_DATASET: the dataset to make on deploy; blank = "<model> · <output>" */
  newDatasetName?: string;
}

export const OUTPUT_DESTINATIONS: { value: OutputDestination; label: string }[] = [
  { value: "NEW_DATASET", label: "New dataset" },
  { value: "DATASET", label: "Existing dataset" },
];

/** The datasets a model's `dataset` fields read: it can't also write them (each upload would queue its next run). */
export function inputDatasetIdsOf(fieldValues: Record<string, unknown> | null | undefined): string[] {
  return Object.values(fieldValues ?? {})
    .map((v) => (v && typeof v === "object" && (v as { type?: string }).type === "dataset" ? (v as { datasetId?: string }).datasetId : null))
    .filter((id): id is string => Boolean(id));
}

/** The destination a ticked output's value asks for: an existing dataset when one was chosen, else a new one. */
export function destinationOf(value: unknown): OutputDestination {
  const d = value && typeof value === "object" ? (value as OutputDestinationValue).destination : undefined;
  return d === "DATASET" ? "DATASET" : "NEW_DATASET";
}

/** The retired "Model output" choice (storageMode SERIES), stored by a wizard from before datasets became the only
 * destination. Never deployed: the run would write a series store that is being retired. */
export const asksForModelStorage = (value: unknown): boolean =>
  Boolean(value && typeof value === "object" && (value as OutputDestinationValue).destination === "MODEL");

/** What's wrong with a ticked output's destination, or null. */
export function destinationProblem(value: unknown, fieldValues?: Record<string, unknown> | null): string | null {
  if (asksForModelStorage(value)) return "A historical model's output goes to a dataset: choose a new or an existing one";
  if (destinationOf(value) !== "DATASET") return null;
  const datasetId = (value as OutputDestinationValue).datasetId;
  if (!datasetId) return "Choose the dataset this output writes";
  if (inputDatasetIdsOf(fieldValues).includes(datasetId)) return "The model reads this dataset; it can't write it too";
  return null;
}

/** The new dataset's name for an output: what was typed, else "<model> · <output>". */
export const newDatasetNameOf = (value: unknown, modelName: string, outputLabel: string): string =>
  ((value as OutputDestinationValue)?.newDatasetName || "").trim() ||
  [modelName.trim(), outputLabel.trim()].filter(Boolean).join(" · ") ||
  "Model output";

/** The duplicate policy a new dataset gets for a template: its runs replace their window (the quality rules) or
 * only add and overwrite cells (a raw feed such as GEFS analysis). `mergePolicy` is the template's historicalConfig. */
export const datasetPolicyFor = (mergePolicy: unknown): "REPLACE_WINDOW" | "LATEST_CELL" =>
  mergePolicy === "REPLACE_WINDOW" ? "REPLACE_WINDOW" : "LATEST_CELL";

/** Why an existing dataset's duplicate policy doesn't suit the template, or null. A warning, not a refusal: the
 * dataset's owner can change the policy in its settings. */
export function datasetPolicyMismatch(datasetPolicy: unknown, mergePolicy: unknown): string | null {
  const wanted = datasetPolicyFor(mergePolicy);
  const actual = datasetPolicy || "LATEST_CELL";
  if (wanted === actual) return null;
  return wanted === "REPLACE_WINDOW"
    ? "this model's runs replace their window, but the dataset keeps every value it has (set its repeated-timestamps policy to Replace window)"
    : "the dataset replaces a window per upload, but this model's runs only add values";
}
